"use client";

import autoAnimate from "@formkit/auto-animate";
import { LuArrowRight } from "react-icons/lu";
import { GetAllEventsOutput } from "~/types/EventTypesInferred";
import { cn } from "~/utils/fontsConfig";
import { EventCard } from "./EventCard";
import ThemedLink from "../themed/ThemedLink";
import EventCardSkeleton from "./EventCardSkeleton";
import { useEffect, useRef, useState } from "react";

interface Props {
    title?: string;
    events?: GetAllEventsOutput[];
    className?: string;
    gridClassName?: string;
    route?: string;
    cols?: number;
    repeatMode?: string;
    titleLink?: string;
    isLoading?: boolean;
    permissions?: {
        isAdmin?: boolean;
        isManager?: boolean;
        isHost?: boolean;
    };
}

export const EventsGrid = ({
    events,
    className,
    gridClassName,
    cols,
    repeatMode = "auto-fit",
    isLoading,
}: Props) => {
    const parent = useRef<HTMLDivElement>(null);
    const [columns, setColumns] = useState(0);

    // calc number of columns so we can
    // span last/orphan element across all columns
    useEffect(() => {
        if (cols) return;
        parent.current && autoAnimate(parent.current);
        const handleResize = () => {
            if (!parent.current) return;
            const containerWidth = parent.current.offsetWidth;
            const item =
                parent.current.querySelector<HTMLElement>("div:first-child");
            if (!item) return;

            const itemStyle = getComputedStyle(item);
            const itemWidth = item.offsetWidth;
            const gap =
                parseInt(itemStyle.marginRight) +
                parseInt(itemStyle.marginLeft);
            setColumns(Math.floor((containerWidth + gap) / (itemWidth + gap)));
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <div
            className={cn(
                "w-full px-2 pb-4 md:bg-transparent md:px-4 lg:px-1 xl:px-0",
                className,
            )}
        >
            <div
                ref={parent}
                className={cn(`grid w-full gap-6`, gridClassName)}
                style={{
                    gridTemplateColumns: cols
                        ? `repeat(${cols}, 1fr)`
                        : `repeat(auto-fill, minmax(280px, 1fr))`,
                }}
            >
                {isLoading ? (
                    <>
                        {[1, 2, 3].map((key) => (
                            <EventCardSkeleton key={key} />
                        ))}
                    </>
                ) : (
                    <>
                        {events?.map((event) => (
                            <EventCard key={event.id} event={event} />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

interface EventsGridWithTitleProps extends Props {
    title: string;
    titleLink: string;
    moreLink?: string;
}

export const EventsGridWithTitle = ({
    events,
    className,
    cols,
    title,
    titleLink,
    moreLink,
    isLoading,
}: EventsGridWithTitleProps) => {
    return (
        <div className={cn("flex flex-col", className)}>
            <div className="flex items-center justify-between px-4 xl:px-0">
                {titleLink && (
                    <div className="flex w-full items-center justify-between">
                        <ThemedLink
                            variant="secondary"
                            href={titleLink ?? ""}
                            className="flex w-full no-underline"
                        >
                            <h5>{title}</h5>
                            <span className="flex h-[40px] items-center md:items-end">
                                <LuArrowRight className="text-[18px] lg:text-[32px]" />
                            </span>
                        </ThemedLink>
                        <ThemedLink
                            variant="secondary"
                            href={moreLink ?? ""}
                            className="hidden min-w-max no-underline hover:underline sm:block"
                        >
                            More events
                        </ThemedLink>
                    </div>
                )}
            </div>
            <EventsGrid events={events} cols={cols} isLoading={isLoading} />
        </div>
    );
};
